(function () {
  'use strict';

  const STEPS = Object.freeze(['Nennweite', 'Höhe', 'Wandabstand', 'Zusammenfassung']);

  function parseJson(element) {
    try {
      return JSON.parse(element?.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  class JeremiasDwGreyConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.rules = window.JeremiasDwGreyRules;
      this.settings = parseJson(this.querySelector('[data-dwgrey-settings]'));
      this.catalog = parseJson(this.querySelector('[data-dwgrey-catalog]'));
      this.app = this.querySelector('[data-dwgrey-app]');
      if (!this.rules || !this.app) {
        if (this.app) this.app.innerHTML = '<p class="chimney-configurator__error" role="alert">Der Konfigurator konnte nicht geladen werden.</p>';
        return;
      }

      this.selection = Object.assign(this.rules.defaultState(), {
        dn: Number(this.settings.initialDn || 150),
        heightClassId: String(this.settings.initialHeightClassId || 'H7'),
        wallDistanceMm: Number(this.settings.initialWallDistanceMm || 100)
      });
      this.previewOnly = this.settings.previewOnly === true;
      this.step = 0;
      this.returnToSummary = false;
      this.partsOpen = false;
      this.submitting = false;
      this.cartMessage = '';
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('toggle', (event) => {
        if (event.target.matches?.('.chimney-configurator__parts')) this.partsOpen = event.target.open;
      }, true);
      this.render();
    }

    resolve() {
      return this.rules.resolveConfiguration(this.selection, this.catalog);
    }

    money(cents) {
      if (!Number.isInteger(cents)) return 'Derzeit nicht verfügbar';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency', currency: this.settings.currency || 'EUR'
      }).format(cents / 100);
    }

    millimetres(value) {
      return `${new Intl.NumberFormat('de-DE').format(Number(value))} mm`;
    }

    render() {
      const result = this.resolve();
      this.result = result;
      this.dataset.ready = String(result.cartEligible);
      this.dataset.step = String(this.step + 1);
      this.dataset.priceCents = Number.isInteger(result.totalPriceCents) ? String(result.totalPriceCents) : '';
      const progress = Math.round(((this.step + 1) / STEPS.length) * 100);

      this.app.innerHTML = `
        <div class="chimney-configurator__head">
          <div class="chimney-configurator__head-system">
            <strong class="chimney-configurator__config-title">Jeremias DW-GREY Bausatz</strong>
            <span class="chimney-configurator__diameter">DN ${result.state.dn} · Wandmontage</span>
          </div>
          <div class="chimney-configurator__head-price">
            <strong class="chimney-configurator__live-price" role="status" aria-live="polite">${this.money(result.totalPriceCents)}</strong>
            <small>Komponenten-Gesamtpreis inkl. MwSt.</small>
          </div>
        </div>
        <div class="chimney-configurator__progress" aria-label="Schritt ${this.step + 1} von ${STEPS.length}">
          <div class="chimney-configurator__progress-row">
            <strong class="chimney-configurator__progress-name">${STEPS[this.step]}</strong>
            <span class="chimney-configurator__progress-count">Schritt ${this.step + 1} von ${STEPS.length}</span>
          </div>
          <i class="chimney-configurator__progress-track" style="--progress:${progress}%"></i>
        </div>
        <div class="chimney-configurator__workspace">
          <aside class="chimney-configurator__preview" aria-live="polite">${this.preview(result)}</aside>
          <form class="chimney-configurator__panel" data-wizard-form novalidate>
            ${this.renderDiameterStep(result)}
            ${this.renderHeightStep(result)}
            ${this.renderDistanceStep(result)}
            ${this.renderSummary(result)}
            <div class="chimney-configurator__navigation">
              ${this.step > 0 ? '<button class="button button--secondary" type="button" data-back>Zurück</button>' : ''}
              ${this.step < STEPS.length - 1 ? `<button class="button" type="button" data-next>${this.returnToSummary ? 'Zur Zusammenfassung' : 'Weiter'}</button>` : ''}
            </div>
          </form>
        </div>`;
      this.dispatchEvent(new CustomEvent('dwgrey:rendered', { bubbles: true, detail: { step: this.step + 1, result } }));
    }

    renderDiameterStep(result) {
      return `<section class="chimney-configurator__step" data-step="0"${this.step !== 0 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Welche Nennweite benötigen Sie?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie die Nennweite passend zu Feuerstätte und Schornsteinplanung.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--diameter">
          ${this.rules.PRIMARY_DNS.map((dn) => this.card({
            name: 'dn', value: dn, selected: result.state.dn === dn,
            title: `DN ${dn}`, hint: `Innen-Ø ${dn} mm`, kind: 'diameter'
          })).join('')}
        </div>
      </section>`;
    }

    renderHeightStep(result) {
      return `<section class="chimney-configurator__step" data-step="1"${this.step !== 1 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Wie hoch soll Ihr Schornstein werden?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie die Höhenklasse. Die technische Aufbauhöhe wird passend zur Nennweite berechnet.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--height dwblack-height-cards">
          ${this.rules.HEIGHT_RECIPES.map((recipe) => this.card({
            name: 'heightClassId', value: recipe.id, selected: result.state.heightClassId === recipe.id,
            title: recipe.customerLabel,
            hint: `Technische Höhe: ${this.millimetres(this.rules.calculateTechnicalHeightMm(result.state.dn, recipe.id))}`,
            kind: 'height'
          })).join('')}
        </div>
      </section>`;
    }

    renderDistanceStep(result) {
      return `<section class="chimney-configurator__step" data-step="2"${this.step !== 2 ? ' hidden' : ''}>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Welchen Wandabstand benötigen Sie?</div>
        <p class="chimney-configurator__step-intro">Wählen Sie den Abstand zwischen Außenwand und Schornsteinsystem. Halter und Wandstütze werden automatisch ermittelt.</p>
        <div class="chimney-configurator__cards chimney-configurator__cards--distance">
          ${this.rules.WALL_DISTANCES_MM.map((distance) => this.card({
            name: 'wallDistanceMm', value: distance, selected: result.state.wallDistanceMm === distance,
            title: `${distance} mm`, kind: 'distance'
          })).join('')}
        </div>
        <p class="chimney-configurator__schematic-note dwblack-mounting-note">Befestigungsuntergrund, Verankerung und die konkrete Einbausituation müssen vor Montage fachlich geprüft werden.</p>
      </section>`;
    }

    renderSummary(result) {
      if (this.step !== 3) return '<section class="chimney-configurator__step" data-step="3" hidden></section>';
      const errors = result.errors?.length
        ? `<div class="chimney-configurator__error" role="alert">Diese Auswahl ist derzeit nicht bestellbar. Bitte prüfen Sie die Konfiguration.</div>`
        : '';
      return `<section class="chimney-configurator__step" data-step="3" data-dwgrey-summary>
        <div class="chimney-configurator__step-title h3" tabindex="-1">Ihr Jeremias DW-GREY Bausatz</div>
        <p class="chimney-configurator__step-intro">Prüfen Sie Auswahl, Bauteile und Komponenten-Gesamtpreis.</p>
        <div class="chimney-configurator__compact-summary dwblack-selection-summary">
          ${this.summaryChoice('Nennweite', `DN ${result.state.dn}`, 0)}
          ${this.summaryChoice('Höhe', `${result.height?.customerLabel || '–'} · ${this.millimetres(result.height?.technicalMm || 0)}`, 1)}
          ${this.summaryChoice('Wandabstand', `${result.state.wallDistanceMm} mm`, 2)}
        </div>
        <p class="dwblack-summary-system">Jeremias DW-GREY · Anthrazitgrau RAL 7016 · doppelwandig · gerade Außenwandführung</p>
        <p class="dwblack-summary-scope"><strong>Standardkonfiguration:</strong> T-Anschluss mit integrierter Wassernase, automatische Halter- und Wandstützenwahl, Mündungsabschluss. Versätze und Dachdurchführungen sind nicht enthalten.</p>
        <div class="chimney-configurator__summary-total" data-dwgrey-total>
          <small>Komponenten-Gesamtpreis</small><strong>${this.money(result.totalPriceCents)}</strong><p>Summe der tatsächlich verwendeten Shopify-Komponentenpreise</p>
        </div>
        <p class="dwfu-delivery"><span aria-hidden="true">✓</span><span><strong>Lieferzeit: ca. 1–2 Wochen</strong><small>Für die enthaltenen Systemkomponenten</small></span></p>
        <details class="chimney-configurator__parts"${this.partsOpen ? ' open' : ''}>
          <summary>Enthaltene Bauteile</summary>
          <ul class="dwfu-parts-list" data-dwgrey-bom>${result.bom.map((line) => `<li data-sku="${escapeHtml(line.sku)}" data-quantity="${line.quantity}"><strong>${line.quantity} × ${escapeHtml(line.label)}</strong><small>${escapeHtml(line.sku)}</small><em>${this.money(Number.isInteger(line.linePriceCents) ? line.linePriceCents : null)}</em></li>`).join('')}</ul>
        </details>
        <div class="dwfu-advisory dwblack-summary-notices">
          <p>Der Übergang zur Feuerstätte bzw. das Rauchrohr ist nicht enthalten und muss projektbezogen separat gewählt werden.</p>
          <p>Die Spannbänder sind Bestandteil der Systemelemente und werden nicht als eigene Warenkorbposition geführt.</p>
          <p>Befestigungsuntergrund, zugelassene Anker und die finale Ausführung sind vor Ort fachlich zu prüfen.</p>
        </div>
        ${errors}
        <button class="button button--full-width" type="button" data-add-configuration${this.previewOnly || !result.cartEligible || this.submitting ? ' disabled' : ''}>${this.submitting ? 'Wird hinzugefügt …' : 'Bausatz in den Warenkorb'}</button>
        <p class="chimney-configurator__cart-note">Die tatsächlichen Komponenten werden mit den berechneten Mengen gemeinsam in den Warenkorb gelegt.</p>
        ${this.cartMessage ? `<div class="chimney-configurator__error" role="alert">${escapeHtml(this.cartMessage)}</div>` : ''}
      </section>`;
    }

    card({ name, value, selected, title, hint, kind }) {
      return `<label class="chimney-configurator__card chimney-configurator__card--compact chimney-configurator__card--${kind}${selected ? ' is-selected' : ''}">
        <input type="radio" name="${escapeHtml(name)}" data-selection="${escapeHtml(name)}" value="${escapeHtml(value)}"${selected ? ' checked' : ''}>
        <strong>${escapeHtml(title)}</strong>${hint ? `<small>${escapeHtml(hint)}</small>` : ''}
      </label>`;
    }

    summaryChoice(label, value, step) {
      return `<span><small>${escapeHtml(label)}</small><strong>${escapeHtml(value)}</strong><button type="button" data-edit-step="${step}" aria-label="${escapeHtml(label)} ändern">Ändern</button></span>`;
    }

    preview(result) {
      const totalHeight = result.height?.technicalMm || 1800;
      const pipeBottom = 442;
      const supportStrokeWidth = 7;
      const supportBottomY = pipeBottom - supportStrokeWidth / 2;
      const pipeTop = Math.max(48, 215 - ((totalHeight - 1797) / (8461 - 1797)) * 150);
      const wallX = 176;
      const distance = Math.max(40, Math.min(88, 34 + result.state.wallDistanceMm / 7));
      const pipeWidth = 34;
      const pipeX = wallX - distance - pipeWidth;
      const pipeCenter = pipeX + pipeWidth / 2;
      const holderCount = Math.max(1, Math.min(5, result.holder?.quantity || 1));
      const topHolderY = Math.min(368, pipeTop + (Math.min(2000, totalHeight) / totalHeight) * (pipeBottom - pipeTop));
      const holders = Array.from({ length: holderCount }, (_, index) => {
        const y = holderCount === 1 ? 368 : 368 - (index / (holderCount - 1)) * (368 - topHolderY);
        return `<g aria-hidden="true"><line x1="${pipeX + pipeWidth}" y1="${y}" x2="${wallX}" y2="${y}" class="dwblack-preview-metal-line"/><rect x="${wallX - 3}" y="${y - 7}" width="6" height="14" class="dwblack-preview-metal"/><circle cx="${pipeCenter}" cy="${y}" r="19" class="dwblack-preview-holder"/></g>`;
      }).join('');
      const segmentCount = Math.max(3, Math.min(9, Math.round(totalHeight / 1000)));
      const segments = Array.from({ length: segmentCount }, (_, index) => {
        const y = pipeBottom - ((pipeBottom - pipeTop) / segmentCount) * (index + 1);
        return `<line x1="${pipeX - 2}" y1="${y}" x2="${pipeX + pipeWidth + 2}" y2="${y}" class="dwblack-preview-joint"/>`;
      }).join('');

      return `<div class="chimney-configurator__preview-stage">
        <p class="chimney-configurator__preview-label">Vorschau</p>
        <svg viewBox="0 0 360 520" role="img" aria-label="Schematische Vorschau eines grauen Jeremias DW-GREY Edelstahlschornsteins">
          <path d="M ${wallX} 94 H 356 V 468 H ${wallX} Z" class="dwfu-preview-building"/>
          <rect x="226" y="142" width="72" height="74" class="dwfu-preview-window"/><line x1="262" y1="142" x2="262" y2="216" class="dwblack-preview-window-line"/><line x1="226" y1="179" x2="298" y2="179" class="dwblack-preview-window-line"/>
          <rect x="226" y="282" width="72" height="74" class="dwfu-preview-window"/><line x1="262" y1="282" x2="262" y2="356" class="dwblack-preview-window-line"/><line x1="226" y1="319" x2="298" y2="319" class="dwblack-preview-window-line"/>
          ${holders}<path d="M ${pipeX + pipeWidth} 425 H ${wallX} V ${supportBottomY} H ${pipeX + pipeWidth}" class="chimney-configurator__preview-support dwblack-preview-support"/><rect x="${wallX - 5}" y="414" width="10" height="38" class="dwblack-preview-metal"/>
          <rect x="${pipeX}" y="${pipeTop}" width="${pipeWidth}" height="${pipeBottom - pipeTop}" class="dwblack-preview-pipe"/>
          <rect x="${pipeX + 7}" y="${pipeTop + 3}" width="5" height="${pipeBottom - pipeTop - 6}" class="dwblack-preview-highlight"/>
          ${segments}
          <path d="M ${pipeX + pipeWidth} 395 H ${wallX + 25}" class="dwblack-preview-tee-outer"/><path d="M ${pipeX + pipeWidth} 395 H ${wallX + 25}" class="dwblack-preview-tee-inner"/>
          <rect x="${pipeX + 5}" y="407" width="24" height="33" rx="2" class="dwblack-preview-pipe"/>
          <path d="M ${pipeX - 7} ${pipeTop} H ${pipeX + pipeWidth + 7} L ${pipeX + pipeWidth + 1} ${pipeTop - 16} H ${pipeX - 1} Z" class="dwblack-preview-pipe"/>
          <line x1="42" y1="${pipeTop}" x2="42" y2="${pipeBottom}" class="dwfu-preview-measure"/><path d="M 37 ${pipeTop + 8} L 42 ${pipeTop} L 47 ${pipeTop + 8} M 37 ${pipeBottom - 8} L 42 ${pipeBottom} L 47 ${pipeBottom - 8}" class="dwfu-preview-measure"/><text x="34" y="${(pipeTop + pipeBottom) / 2}" class="dwfu-preview-measure-text" transform="rotate(-90 34 ${(pipeTop + pipeBottom) / 2})">${escapeHtml(result.height?.customerLabel || '')}</text>
          <line x1="${pipeX + pipeWidth}" y1="478" x2="${wallX}" y2="478" class="dwfu-preview-distance"/><path d="M ${pipeX + pipeWidth + 5} 474 L ${pipeX + pipeWidth} 478 L ${pipeX + pipeWidth + 5} 482 M ${wallX - 5} 474 L ${wallX} 478 L ${wallX - 5} 482" class="dwfu-preview-distance"/><text x="${(pipeX + pipeWidth + wallX) / 2}" y="497" class="dwfu-preview-distance-text">${result.state.wallDistanceMm} mm</text>
        </svg>
      </div>
      <div class="chimney-configurator__preview-meta dwblack-preview-meta">
        <span><small>Höhenklasse</small><strong>${escapeHtml(result.height?.customerLabel || '–')}</strong></span>
        <span><small>Technische Höhe</small><strong>${this.millimetres(totalHeight)}</strong></span>
        <span><small>Nennweite</small><strong>DN ${result.state.dn}</strong></span>
        <span><small>Wandabstand</small><strong>${result.state.wallDistanceMm} mm</strong></span>
      </div>`;
    }

    onChange(event) {
      const input = event.target;
      const key = input.dataset.selection;
      if (!key) return;
      this.selection[key] = key === 'heightClassId' ? String(input.value) : Number(input.value);
      this.cartMessage = '';
      this.render();
    }

    async onClick(event) {
      if (event.target.closest('[data-next]')) {
        this.step = this.returnToSummary ? 3 : Math.min(3, this.step + 1);
        this.returnToSummary = false;
        this.render();
        this.focusStep();
        return;
      }
      if (event.target.closest('[data-back]')) {
        this.step = Math.max(0, this.step - 1);
        this.render();
        this.focusStep();
        return;
      }
      const edit = event.target.closest('[data-edit-step]');
      if (edit) {
        this.step = Math.max(0, Math.min(2, Number(edit.dataset.editStep)));
        this.returnToSummary = true;
        this.render();
        this.focusStep();
        return;
      }
      if (event.target.closest('[data-add-configuration]') && !this.previewOnly) await this.addToCart();
    }

    focusStep() {
      requestAnimationFrame(() => this.querySelector('.chimney-configurator__step:not([hidden]) .chimney-configurator__step-title')?.focus?.());
    }

    async addToCart() {
      const result = this.resolve();
      if (!result.cartEligible || this.submitting) return;
      this.submitting = true;
      this.cartMessage = '';
      this.render();
      try {
        const payload = this.rules.buildCartPayload(result, `dw-grey-${Date.now().toString(36)}`);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.description || 'Die Konfiguration konnte nicht in den Warenkorb gelegt werden.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'jeremias-dw-grey-configurator' } }));
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        this.submitting = false;
        this.cartMessage = error.message || 'Die Konfiguration konnte nicht in den Warenkorb gelegt werden.';
        this.render();
      }
    }
  }

  if (!customElements.get('jeremias-dw-grey-configurator')) {
    customElements.define('jeremias-dw-grey-configurator', JeremiasDwGreyConfigurator);
  }
})();
